import assert from 'assert'
import React, { ReactNode, useContext, useEffect, useState } from 'react'
import styled from '@emotion/styled'

import { RadioGroupContext, RadioItemContext } from './RadioGroup'
import { Box } from '../Box/Box'
import { Icon } from '../Icon/Icon'
import { Badge, BadgeProps } from '../Badge/Badge'

export interface RadioItemProps {
  label?: string
  value: string
  badge?: { label: string; tone: BadgeProps['tone']; align: 'start' | 'end' }
  alignRadioInput?:
    | 'start'
    | 'center'
    | 'end'
    | 'self-start'
    | 'self-end'
    | 'flex-start'
    | 'flex-end'
    | 'auto'
    | 'normal'
  onRemove?: () => void
  disabled?: boolean
  ChildRadioGroup?: ReactNode
}

export const RadioItem: React.FC<RadioItemProps> = ({
  label,
  value,
  badge,
  onRemove,
  alignRadioInput = 'center',
  disabled = false,
  ChildRadioGroup,
  ...props
}) => {
  assert(label || props.children, 'RadioItem must have a label or child component(s).')

  const context = useContext(RadioGroupContext)
  const index = useContext(RadioItemContext)

  if (!context) throw new Error('All RadioItems must be children of a RadioGroup.')

  const id = `${context.name}_${index}`
  const checked = context.value === value

  const [growLabel, setGrowLabel] = useState(1)
  useEffect(() => {
    if (badge && badge.align === 'start') {
      setGrowLabel(0)
    }
  }, [])

  return (
    <Wrapper checked={checked} paddingX={context.itemPaddingX} disabled={disabled} {...props}>
      <Radio
        type="radio"
        id={id}
        name={context.name}
        checked={checked}
        value={value}
        onChange={context.onChange}
        disabled={disabled}
      />
      <Box display="flex" justifyContent="between" alignItems="center">
        <Box display="flex" alignItems="center" flexGrow={growLabel}>
          <OuterCircle checked={checked} alignRadioInput={alignRadioInput}>
            <InnerCircle className="inner-circle" checked={checked} />
          </OuterCircle>
          <Label htmlFor={id}>{label || props.children}</Label>
        </Box>

        {badge && (
          <Box marginLeft="m" flexGrow={badge.align === 'start' ? 1 : 0}>
            <Badge tone={badge.tone}>{badge.label}</Badge>
          </Box>
        )}

        {onRemove && (
          <Box marginLeft="m" component="button" onClick={onRemove}>
            <Icon size="m" tone="negative" name="close" />
          </Box>
        )}
      </Box>
      {checked && ChildRadioGroup}
    </Wrapper>
  )
}

const Wrapper = styled(Box)<{ checked: boolean; disabled?: boolean }>`
  position: relative;

  ${(p) =>
    p.disabled &&
    `
    cursor: not-allowed;
    opacity: 0.6;
  `}

  ${(p) =>
    !p.disabled &&
    (p.checked ||
      `
    &:hover .inner-circle {
      opacity: 1;
      background: var(--colors-subtle);
    }
    `)}
`

const Radio = styled.input`
  position: absolute;
  top: -2px;
  left: -2px;
  width: 36px;
  height: 24px;
  opacity: 0;
  margin: 0;
  cursor: pointer;
`

interface CheckedProp {
  checked: boolean
  alignRadioInput?: string
}

const OuterCircle = styled.div<CheckedProp>`
  position: relative;
  height: 20px;
  width: 20px;
  min-width: 20px;
  border-radius: 50%;
  border: 2px solid var(--colors-subtle);
  pointer-events: none;
  transition: all var(--transition);
  align-self: self-start;
  ${(p) =>
    p.alignRadioInput &&
    `
    align-self: ${p.alignRadioInput};
  `}
  ${(p) =>
    p.checked &&
    `
    border-color: var(--colors-action);
  `}
`

const InnerCircle = styled.div<CheckedProp>`
  position: absolute;
  top: 2px;
  left: 2px;
  height: 12px;
  width: 12px;
  border-radius: 50%;
  opacity: 0;
  transition: all var(--transition);
  ${(p) =>
    p.checked &&
    `
    background: var(--colors-action);
    opacity: 1;
  `}
`

const Label = styled.label`
  display: block;
  cursor: pointer;
  user-select: none;
  padding-left: var(--space--s);
  flex-grow: 1;
`
